// Player/Credits: MUI styled → inline styles.
import { useEffect, useState } from "react";

// Unsplash API guideline: attribution links require utm_campaign=api-credit too.
const UNSPLASH_UTM = '?utm_source=scrolly&utm_medium=referral&utm_campaign=api-credit';

/**
 * Dauer der Blende in Millisekunden.
 *
 * Jans Vorgabe 05.08.2026: „Fotocredits leicht ein- und ausblenden von Slide zu
 * Slide. Momentan springt es von einem Slide zum anderen." Kurz genug, dass der
 * Nachweis beim Scrollen mitkommt, lang genug, dass es nicht wieder wie ein
 * Sprung wirkt.
 */
const BLENDE_MS = 220;

interface Eintrag {
    readonly text: string;
    readonly photographerUrl?: string;
}

interface Props {
    readonly light: boolean;
    readonly text: string;
    readonly photographerUrl?: string;
    /** Weitere Nachweise derselben Slide (Jan 13.10.2026: zwei Bilder). */
    readonly weitere?: ReadonlyArray<Eintrag>;
}

export default function Credits({ light, text, photographerUrl, weitere }: Props) {
    /**
     * Der gerade GEZEIGTE Nachweis. Er hinkt den Angaben absichtlich hinterher:
     * Erst blendet der alte aus, dann wird getauscht, dann blendet der neue ein
     * — sonst stünde beim Wechsel für einen Moment der neue Text in der alten
     * Blende.
     */
    const weitereSig = JSON.stringify(weitere ?? []);
    const [gezeigt, setGezeigt] = useState({ light, text, photographerUrl, weitereSig });
    const [ausblenden, setAusblenden] = useState(false);
    const [zuletzt, setZuletzt] = useState({ light, text, photographerUrl, weitereSig });

    // Änderung beim RENDERN erkennen, nicht in einem Effect: so entsteht kein
    // zusätzlicher Durchlauf, und die Blende beginnt im selben Bild.
    const geaendert =
        zuletzt.text !== text ||
        zuletzt.light !== light ||
        zuletzt.photographerUrl !== photographerUrl ||
        zuletzt.weitereSig !== weitereSig;
    if (geaendert) {
        setZuletzt({ light, text, photographerUrl, weitereSig });
        setAusblenden(true);
    }

    useEffect(() => {
        if (!ausblenden) return;
        const timer = setTimeout(() => {
            setGezeigt(zuletzt);
            setAusblenden(false);
        }, BLENDE_MS);
        return () => clearTimeout(timer);
    }, [ausblenden, zuletzt]);

    const color = `hsl(0deg 0% ${gezeigt.light ? "100%" : "0%"} / 0.5)`;
    const linkColor = `hsl(0deg 0% ${gezeigt.light ? "100%" : "0%"} / 0.85)`;

    // Ein Nachweis: „Foto: Name / Unsplash" mit Fotograf-URL → zwei Links mit
    // UTM (Unsplash-Richtlinie); sonst ein FREIER Link um den ganzen Text
    // (nur http/https); ohne Link reiner Text.
    const linkStil = { color: linkColor, textDecoration: "underline", cursor: "pointer" } as const;
    const eintragRendern = (e: Eintrag, key: number) => {
        const u = e.photographerUrl;
        const m = u ? e.text.match(/^(Foto:\s*)(.+?)(\s*\/\s*)(Unsplash)$/) : null;
        if (m) {
            return (
                <span key={key}>
                    {m[1]}
                    <a href={`${u}${UNSPLASH_UTM}`} target="_blank" rel="noopener noreferrer" style={linkStil}>{m[2]}</a>
                    {m[3]}
                    <a href={`https://unsplash.com${UNSPLASH_UTM}`} target="_blank" rel="noopener noreferrer" style={linkStil}>{m[4]}</a>
                </span>
            );
        }
        if (u && /^https?:\/\//i.test(u)) {
            return (
                <a key={key} href={u} target="_blank" rel="noopener noreferrer" style={linkStil}>{e.text}</a>
            );
        }
        return <span key={key}>{e.text}</span>;
    };
    let weitereGezeigt: Eintrag[] = [];
    try {
        weitereGezeigt = JSON.parse(gezeigt.weitereSig) as Eintrag[];
    } catch {
        weitereGezeigt = [];
    }
    const eintraege: Eintrag[] = [
        { text: gezeigt.text, photographerUrl: gezeigt.photographerUrl },
        ...weitereGezeigt,
    ].filter((e) => (e.text ?? "").trim());
    const hatLink = eintraege.some((e) => !!e.photographerUrl);

    // The wrapper in Renderer.tsx is `pointer-events: none` so the empty
    // area around the text stays scroll-transparent. We re-enable pointer
    // events only on the inline-block span that contains the link text —
    // Safari does not reliably honour a child's pointer-events override
    // when the ancestor chain passes through multiple `none` nodes.
    return (
        <div
            style={{
                width: "100%",
                padding: "8px 0",
                pointerEvents: "none",
                textAlign: "center",
                // Ohne Text unsichtbar — der Knoten bleibt aber stehen, damit
                // das Ausblenden ueberhaupt zu sehen ist.
                opacity: ausblenden || !eintraege.length ? 0 : 1,
                transition: `opacity ${BLENDE_MS}ms ease`,
            }}
        >
            <span
                style={{
                    color,
                    display: "inline-block",
                    pointerEvents: hatLink ? "auto" : undefined,
                }}
            >
                {eintraege.map((e, i) => (
                    <span key={i}>
                        {i > 0 ? " · " : null}
                        {eintragRendern(e, i)}
                    </span>
                ))}
            </span>
        </div>
    );
}
